import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CalendlyEmbed from '@/components/CalendlyEmbed';

const KickoffMeeting: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [meetingScheduled, setMeetingScheduled] = useState(false);

  // Get prefill email from URL params if available
  const urlParams = new URLSearchParams(window.location.search);
  const prefillEmail = urlParams.get('email') || '';

  const handleEventScheduled = async () => {
    setMeetingScheduled(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900 flex flex-col">
      <Header />
      
      <div className="flex-1 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">
              {meetingScheduled
                ? t('kickoffMeeting.header.scheduled')
                : t('kickoffMeeting.header.schedule')}
            </h1>
            <p className="text-brand-secondary text-lg">
              {meetingScheduled
                ? t('kickoffMeeting.subtitle.scheduled')
                : t('kickoffMeeting.subtitle.schedule')}
            </p>
          </div>

          {meetingScheduled ? (
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 sm:p-10 border border-white/20">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-secondary/20 mb-6">
                  <svg
                    className="w-10 h-10 text-brand-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('kickoffMeeting.confirmed.congratulations')}
                </h2>
                <p className="text-xl text-brand-secondary mb-2">
                  {t('kickoffMeeting.confirmed.welcome')}
                </p>
              </div>

              {/* Main Content */}
              <div className="space-y-6 text-white/90 leading-relaxed">
                <p className="text-lg">
                  {t('kickoffMeeting.confirmed.thankYou')}
                </p>

                <p className="text-lg">
                  {t('kickoffMeeting.confirmed.meetingConfirmed')}
                </p>

                {/* Next Steps Section */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {t('kickoffMeeting.confirmed.nextSteps.title')}
                  </h3>
                  <p className="mb-6">
                    {t('kickoffMeeting.confirmed.nextSteps.intro')}
                  </p>

                  {/* Meeting Section */}
                  <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">
                      {t('kickoffMeeting.confirmed.nextSteps.meeting.title')}
                    </h4>
                    <div className="space-y-3">
                      <p>
                        {t('kickoffMeeting.confirmed.nextSteps.meeting.objective').includes(':') ? (
                          <>
                            <span className="font-semibold text-brand-secondary">
                              {t('kickoffMeeting.confirmed.nextSteps.meeting.objective').split(':')[0]}:
                            </span>
                            {t('kickoffMeeting.confirmed.nextSteps.meeting.objective').split(':')[1]}
                          </>
                        ) : (
                          t('kickoffMeeting.confirmed.nextSteps.meeting.objective')
                        )}
                      </p>
                      <p>
                        {t('kickoffMeeting.confirmed.nextSteps.meeting.agenda').includes(':') ? (
                          <>
                            <span className="font-semibold text-brand-secondary">
                              {t('kickoffMeeting.confirmed.nextSteps.meeting.agenda').split(':')[0]}:
                            </span>
                            {t('kickoffMeeting.confirmed.nextSteps.meeting.agenda').split(':')[1]}
                          </>
                        ) : (
                          t('kickoffMeeting.confirmed.nextSteps.meeting.agenda')
                        )}
                      </p>
                      <p>
                        {t('kickoffMeeting.confirmed.nextSteps.meeting.duration').includes(':') ? (
                          <>
                            <span className="font-semibold text-brand-secondary">
                              {t('kickoffMeeting.confirmed.nextSteps.meeting.duration').split(':')[0]}:
                            </span>
                            {t('kickoffMeeting.confirmed.nextSteps.meeting.duration').split(':')[1]}
                          </>
                        ) : (
                          t('kickoffMeeting.confirmed.nextSteps.meeting.duration')
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Preparation Section */}
                  <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
                    <h4 className="text-xl font-bold text-white mb-4">
                      {t('kickoffMeeting.confirmed.nextSteps.preparation.title')}
                    </h4>
                    <ul className="space-y-3 list-disc list-inside">
                      <li>{t('kickoffMeeting.confirmed.nextSteps.preparation.point1')}</li>
                      <li>{t('kickoffMeeting.confirmed.nextSteps.preparation.point2')}</li>
                    </ul>
                  </div>
                </div>

                {/* Commitment Message */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-lg italic text-white/80">
                    {t('kickoffMeeting.confirmed.commitment')}
                  </p>
                </div>

                {/* Contact Information */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <h4 className="text-xl font-bold text-white mb-4">
                    {t('kickoffMeeting.confirmed.contact.title')}
                  </h4>
                  <p className="mb-2">
                    {t('kickoffMeeting.confirmed.contact.message')}
                  </p>
                  <p className="text-brand-secondary font-semibold mb-4">
                    {t('kickoffMeeting.confirmed.contact.email')}
                  </p>
                  <p className="text-white/80">
                    {t('kickoffMeeting.confirmed.contact.closing')}
                  </p>
                </div>
              </div>

              {/* Return Button */}
              <div className="mt-8 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="px-8 py-3 bg-gradient-to-r from-brand-secondary to-brand-primary text-white rounded-lg font-medium hover:from-brand-primary hover:to-brand-secondary transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/40"
                >
                  {t('kickoffMeeting.actions.returnToHome')}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <div className="bg-white rounded-lg p-4">
                <CalendlyEmbed
                  url="https://calendly.com/agents-inscius/60min"
                  prefill={{ email: prefillEmail }}
                  onEventScheduled={handleEventScheduled}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default KickoffMeeting;
